import { db } from '@/database/db';
import ApiService from './ApiService';
import { syncService } from './sync.service';

/**
 * Service pour charger initialement toutes les données de référence
 * nécessaires pour le mode offline
 */
class OfflineSyncInitialService {
  private isSyncing = false;
  private syncProgress = {
    current: 0,
    total: 0,
    currentEntity: ''
  };

  /**
   * Obtient le progrès de synchronisation
   */
  getProgress() {
    return { ...this.syncProgress };
  }

  /**
   * Vérifie si une synchronisation est en cours
   */
  isSyncingData(): boolean {
    return this.isSyncing;
  }
  /**
   * Charge toutes les données de référence au démarrage
   */
  async loadAllReferenceData(): Promise<void> {
    if (syncService.isOffline()) {
      console.log('Mode offline: données déjà chargées depuis IndexedDB');
      return;
    }

    try {
      console.log('Chargement des données de référence pour le mode offline...');

      // Charger toutes les entités en parallèle
      await Promise.all([
        this.loadModePaiements(),
        this.loadSecteurs(),
        this.loadZones(),
        this.loadMarches(),
        this.loadActivites(),
        this.loadRoles(),
        this.loadPermissions(),
        this.loadUsers(),
      ]);

      console.log('✅ Toutes les données de référence ont été chargées');
    } catch (error) {
      console.error('Erreur lors du chargement des données de référence:', error);
    }
  }

  /**
   * Duplique toutes les données principales depuis l'API vers IndexedDB
   * Cette fonction est appelée lors de l'activation du mode offline
   */
  async duplicateAllData(): Promise<{ success: boolean; message: string; stats?: any }> {
    if (this.isSyncing) {
      return { success: false, message: 'Une synchronisation est déjà en cours' };
    }

    if (syncService.isOffline()) {
      return { success: false, message: 'Déjà en mode offline' };
    }

    this.isSyncing = true;
    this.syncProgress = { current: 0, total: 0, currentEntity: '' };

    const stats = {
      contrats: 0,
      paiements: 0,
      paiementsRecette: 0,
      arrieresElectronic: 0,
      usagers: 0,
      emplacements: 0,
      errors: [] as string[]
    };

    try {
      console.log('🔄 Début de la duplication de la base de données...');

      // Liste des entités à charger avec leurs endpoints
      const entitiesToLoad = [
        { name: 'Modes de paiement', endpoint: '/api/listeMode', method: 'get', table: db.modePaiements },
        { name: 'Secteurs', endpoint: '/api/liste/secteur', method: 'post', table: db.secteurs, body: {} },
        { name: 'Zones', endpoint: '/api/zones', method: 'get', table: db.zones },
        { name: 'Marchés', endpoint: '/api/marches', method: 'get', table: db.marches },
        { name: 'Activités', endpoint: '/api/listeActivite', method: 'get', table: db.activites },
        { name: 'Rôles', endpoint: '/api/roles', method: 'get', table: db.roles },
        { name: 'Permissions', endpoint: '/api/all/permissions', method: 'get', table: db.permissions },
        { name: 'Utilisateurs', endpoint: '/api/liste/users', method: 'post', table: db.users, body: { page: 1, limit: 1000 } },
        { name: 'Usagers', endpoint: '/api/listeUsagers', method: 'post', table: db.usagers, body: { page: 1, limit: 1000 } },
        { name: 'Emplacements', endpoint: '/api/listeEmplacement', method: 'post', table: db.emplacements, body: { page: 1, limit: 1000 } },
        { name: 'Contrats', endpoint: '/api/contrats/liste', method: 'post', table: db.contrats, body: { page: 1, limit: 1000 } },
        { name: 'Paiements', endpoint: '/api/listePaiement', method: 'post', table: db.paiements, body: { page: 1, limit: 1000 } },
        { name: 'Paiements Recette', endpoint: '/api/listePaiement/autre/recette', method: 'post', table: db.paiementsRecette, body: { page: 1, limit: 1000 } },
      ];

      this.syncProgress.total = entitiesToLoad.length;

      // Charger chaque entité séquentiellement pour éviter la surcharge
      for (let i = 0; i < entitiesToLoad.length; i++) {
        const entity = entitiesToLoad[i];
        this.syncProgress.current = i + 1;
        this.syncProgress.currentEntity = entity.name;

        try {
          let response: any;
          
          if (entity.method === 'get') {
            response = await ApiService.get(entity.endpoint, '');
          } else {
            response = await ApiService.post(entity.endpoint, entity.body || {});
          }

          if (response.data && response.data.success) {
            let dataToSave: any[] = [];

            // Extraire les données selon le format de réponse
            if (Array.isArray(response.data.data)) {
              dataToSave = response.data.data;
            } else if (response.data.data?.data && Array.isArray(response.data.data.data)) {
              dataToSave = response.data.data.data;
            } else if (response.data.data && typeof response.data.data === 'object') {
              dataToSave = [response.data.data];
            }

            if (dataToSave.length > 0) {
              const entities = dataToSave.map((item: any) => ({
                ...item,
                syncStatus: 'synced' as const,
                lastSync: new Date(),
                createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
                updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date(),
                version: item.version || 1
              }));

              // Utiliser bulkPut avec un cast pour éviter les erreurs TypeScript
              await (entity.table as any).bulkPut(entities);

              // Mettre à jour les statistiques
              const entityName = entity.name.toLowerCase();
              if (entityName.includes('contrat')) stats.contrats = entities.length;
              else if (entityName.includes('paiement') && entityName.includes('recette')) stats.paiementsRecette = entities.length;
              else if (entityName.includes('paiement')) stats.paiements = entities.length;
              else if (entityName.includes('usager')) stats.usagers = entities.length;
              else if (entityName.includes('emplacement')) stats.emplacements = entities.length;

              console.log(`✅ ${entity.name}: ${entities.length} éléments chargés`);
            }
          }
        } catch (error: any) {
          const errorMsg = `Erreur lors du chargement de ${entity.name}: ${error.message}`;
          console.error(`❌ ${errorMsg}`);
          stats.errors.push(errorMsg);
        }

        // Petit délai pour éviter la surcharge
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Charger les arrières électroniques par contrat (si nécessaire)
      try {
        this.syncProgress.currentEntity = 'Arrières électroniques';
        // Cette opération peut être longue, on la fait en arrière-plan
        // ou on peut la charger à la demande
        console.log('ℹ️ Les arrières électroniques seront chargés à la demande');
      } catch (error: any) {
        stats.errors.push(`Erreur arrières: ${error.message}`);
      }

      console.log('✅ Duplication terminée:', stats);
      return {
        success: true,
        message: 'Duplication terminée avec succès',
        stats
      };
    } catch (error: any) {
      console.error('❌ Erreur lors de la duplication:', error);
      return {
        success: false,
        message: `Erreur: ${error.message}`,
        stats
      };
    } finally {
      this.isSyncing = false;
      this.syncProgress = { current: 0, total: 0, currentEntity: '' };
    }
  }

  /**
   * Charge les modes de paiement
   */
  private async loadModePaiements(): Promise<void> {
    try {
      const response = await ApiService.get('/api/listeMode', '');
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        const entities = response.data.data.map((item: any) => ({
          ...item,
          syncStatus: 'synced' as const,
          lastSync: new Date(),
          createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
          updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date(),
          version: item.version || 1
        }));
        await db.modePaiements.bulkPut(entities);
      }
    } catch (error) {
      console.warn('Erreur lors du chargement des modes de paiement:', error);
    }
  }

  /**
   * Charge les secteurs
   */
  private async loadSecteurs(): Promise<void> {
    try {
      const response = await ApiService.post('/api/liste/secteur', {});
      if (response.data && response.data.success) {
        const data = Array.isArray(response.data.data) ? response.data.data : 
                    (response.data.data?.data || []);
        const entities = data.map((item: any) => ({
          ...item,
          syncStatus: 'synced' as const,
          lastSync: new Date(),
          createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
          updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date(),
          version: item.version || 1
        }));
        await db.secteurs.bulkPut(entities);
      }
    } catch (error) {
      console.warn('Erreur lors du chargement des secteurs:', error);
    }
  }

  /**
   * Charge les zones
   */
  private async loadZones(): Promise<void> {
    try {
      // Si une route existe pour les zones
      const response = await ApiService.get('/api/zones', '');
      if (response.data && response.data.success) {
        const data = Array.isArray(response.data.data) ? response.data.data : 
                    (response.data.data?.data || []);
        const entities = data.map((item: any) => ({
          ...item,
          syncStatus: 'synced' as const,
          lastSync: new Date(),
          createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
          updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date(),
          version: item.version || 1
        }));
        await db.zones.bulkPut(entities);
      }
    } catch (error) {
      console.warn('Erreur lors du chargement des zones:', error);
    }
  }

  /**
   * Charge les marchés
   */
  private async loadMarches(): Promise<void> {
    try {
      const response = await ApiService.get('/api/marches', '');
      if (response.data && response.data.success) {
        const data = Array.isArray(response.data.data) ? response.data.data : 
                    (response.data.data?.data || []);
        const entities = data.map((item: any) => ({
          ...item,
          syncStatus: 'synced' as const,
          lastSync: new Date(),
          createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
          updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date(),
          version: item.version || 1
        }));
        await db.marches.bulkPut(entities);
      }
    } catch (error) {
      console.warn('Erreur lors du chargement des marchés:', error);
    }
  }

  /**
   * Charge les activités
   */
  private async loadActivites(): Promise<void> {
    try {
      const response = await ApiService.get('/api/listeActivite', '');
      if (response.data && response.data.success) {
        const data = Array.isArray(response.data.data) ? response.data.data : 
                    (response.data.data?.data || []);
        const entities = data.map((item: any) => ({
          ...item,
          syncStatus: 'synced' as const,
          lastSync: new Date(),
          createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
          updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date(),
          version: item.version || 1
        }));
        await db.activites.bulkPut(entities);
      }
    } catch (error) {
      console.warn('Erreur lors du chargement des activités:', error);
    }
  }

  /**
   * Charge les rôles
   */
  private async loadRoles(): Promise<void> {
    try {
      const response = await ApiService.get('/api/roles', '');
      if (response.data && response.data.success) {
        const data = Array.isArray(response.data.data) ? response.data.data : 
                    (response.data.data?.data || []);
        const entities = data.map((item: any) => ({
          ...item,
          syncStatus: 'synced' as const,
          lastSync: new Date(),
          createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
          updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date(),
          version: item.version || 1
        }));
        await db.roles.bulkPut(entities);
      }
    } catch (error) {
      console.warn('Erreur lors du chargement des rôles:', error);
    }
  }

  /**
   * Charge les permissions
   */
  private async loadPermissions(): Promise<void> {
    try {
      const response = await ApiService.get('/api/all/permissions', '');
      if (response.data && response.data.success) {
        const data = Array.isArray(response.data.data) ? response.data.data : 
                    (response.data.data?.data || []);
        const entities = data.map((item: any) => ({
          ...item,
          syncStatus: 'synced' as const,
          lastSync: new Date(),
          createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
          updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date(),
          version: item.version || 1
        }));
        await db.permissions.bulkPut(entities);
      }
    } catch (error) {
      console.warn('Erreur lors du chargement des permissions:', error);
    }
  }

  /**
   * Charge les utilisateurs
   */
  private async loadUsers(): Promise<void> {
    try {
      const response = await ApiService.post('/api/liste/users', { page: 1, limit: 1000 });
      if (response.data && response.data.success) {
        const data = Array.isArray(response.data.data) ? response.data.data : 
                    (response.data.data?.data || []);
        const entities = data.map((item: any) => ({
          ...item,
          password: undefined, // Ne pas stocker les mots de passe
          syncStatus: 'synced' as const,
          lastSync: new Date(),
          createdAt: item.createdAt ? new Date(item.createdAt) : new Date(),
          updatedAt: item.updatedAt ? new Date(item.updatedAt) : new Date(),
          version: item.version || 1
        }));
        await db.users.bulkPut(entities);
      }
    } catch (error) {
      console.warn('Erreur lors du chargement des utilisateurs:', error);
    }
  }
}

// Instance singleton
export const offlineSyncInitialService = new OfflineSyncInitialService();

